import { useEffect, useState, useCallback, useRef } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Save, Loader2, Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_HOST = process.env.REACT_APP_BACKEND_URL;

export default function BrandingSettings() {
  const { getAccessTokenSilently } = useAdmin();
  const [branding, setBranding] = useState({ header_logo_url: '/logo.png', footer_logo_url: '/logo.png' });
  const [ads, setAds] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const r = await fetch(`${API}/admin/homepage-settings`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      const s = (await r.json()).settings;
      setBranding(s?.branding || { header_logo_url: '/logo.png', footer_logo_url: '/logo.png' });
      setAds(s?.ads?.placeholders || {});
    }
    setLoading(false);
  }, [getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  async function uploadLogo(slot, file) {
    setUploading(slot);
    try {
      const token = await getAccessTokenSilently();
      const fd = new FormData();
      fd.append('file', file);
      let r = await fetch(`${API}/admin/branding/upload-logo?slot=${slot}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        if (d.has_white_bg) {
          if (window.confirm('Slika ima bijelu pozadinu — želite li da je automatski učinimo prozirnom?')) {
            const fd2 = new FormData();
            fd2.append('file', file);
            r = await fetch(`${API}/admin/branding/upload-logo?slot=${slot}&auto_transparent=true`, {
              method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd2,
            });
          }
        }
        toast.success('Logo uploadan');
        await load();
      } else { toast.error('Upload neuspješan'); }
    } catch { toast.error('Upload neuspješan'); }
    finally { setUploading(null); }
  }

  async function saveAds() {
    const token = await getAccessTokenSilently();
    // Fetch current settings to merge
    const r0 = await fetch(`${API}/admin/homepage-settings`, { headers: { Authorization: `Bearer ${token}` } });
    const current = r0.ok ? (await r0.json()).settings : {};
    const r = await fetch(`${API}/admin/homepage-settings`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...current, ads: { placeholders: ads } }),
    });
    if (r.ok) toast.success('Postavke oglasa spremljene');
    else toast.error('Greška');
  }

  if (loading) return <div className="p-10 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;

  const hdrUrl = branding.header_logo_url?.startsWith('/api/') ? `${BACKEND_HOST}${branding.header_logo_url}` : branding.header_logo_url;
  const ftrUrl = branding.footer_logo_url?.startsWith('/api/') ? `${BACKEND_HOST}${branding.footer_logo_url}` : branding.footer_logo_url;

  const LogoCard = ({ slot, label, url, inputRef }) => (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid={`logo-card-${slot}`}>
      <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-3">{label}</h3>
      <div className="flex items-center gap-4 mb-3">
        <div className="w-40 h-16 rounded border border-zinc-700 bg-white flex items-center justify-center overflow-hidden">
          <img src={url} alt="" className="max-h-14" />
        </div>
        <div className="w-40 h-16 rounded border border-zinc-700 bg-[#0b1220] flex items-center justify-center overflow-hidden">
          <img src={url} alt="" className="max-h-14" />
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">Prikazano na svijetloj (lijevo) i tamnoj (desno) pozadini</p>
      <button onClick={() => inputRef.current?.click()} disabled={uploading === slot}
              className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded disabled:opacity-50" data-testid={`logo-upload-${slot}`}>
        {uploading === slot ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
        {uploading === slot ? 'Upload...' : 'Upload novi logo (PNG)'}
      </button>
      <input ref={inputRef} type="file" accept="image/png,image/webp"
             onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(slot, f); e.target.value = ''; }}
             className="hidden" />
    </div>
  );

  return (
    <div className="p-6 max-w-5xl" data-testid="branding-settings-page">
      <h1 className="text-2xl font-bold text-white mb-6">Branding i oglasni prostori</h1>

      <div className="grid md:grid-cols-2 gap-5 mb-8">
        <LogoCard slot="header" label="Header logo" url={hdrUrl} inputRef={headerRef} />
        <LogoCard slot="footer" label="Footer logo" url={ftrUrl} inputRef={footerRef} />
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="ad-slots-config">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-white">Oglasni prostori — uključeno/isključeno</h3>
          <button onClick={saveAds} className="flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded" data-testid="save-ads-btn">
            <Save size={12} /> Spremi
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {Object.entries({
            'homepage-top': 'Naslovnica — vrh (970x250)',
            'homepage-mid': 'Naslovnica — sredina (970x250)',
            'sidebar-rectangle': 'Sidebar rectangle (300x250)',
            'sidebar-halfpage': 'Sidebar half-page (300x600)',
            'article-inline': 'Članak — u tekstu (580x300)',
            'article-bottom': 'Članak — ispod (970x250)',
            'category-top': 'Kategorija — vrh (970x250)',
            'category-sidebar': 'Kategorija — sidebar (300x250)',
          }).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-300">
              <input type="checkbox" checked={ads[key] !== false} onChange={(e) => setAds(a => ({ ...a, [key]: e.target.checked }))}
                     className="accent-red-600" data-testid={`ad-toggle-${key}`} />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-6 p-4 bg-amber-900/20 border border-amber-800/40 rounded-lg text-xs text-amber-200 flex gap-2" data-testid="branding-hint">
        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
        <div>PNG logo trebao bi imati prozirnu pozadinu. Ako upload-ate sliku s bijelom pozadinom, sustav će vas pitati želite li automatski pretvoriti bijelo u prozirnost.</div>
      </div>
    </div>
  );
}
